import { useState } from "react";
import { storage } from "../firebase/config";
import {
    deleteObject,
    getDownloadURL,
    ref as storageRef,
    uploadBytes,
  } from "firebase/storage";

/**
 * Resize an image file client-side using an OffscreenCanvas (or regular canvas).
 * Returns a Blob of the resized image, or null if the input isn't a raster image.
 */
const resizeImage = (file, maxWidth = 400) =>
  new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(null);
    const img = new Image();
    img.onload = () => {
      // Skip if already small enough
      if (img.width <= maxWidth) { resolve(null); return; }
      const scale = maxWidth / img.width;
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob), "image/webp", 0.8);
    };
    img.onerror = () => resolve(null);
    img.src = URL.createObjectURL(file);
  });

export const useStorage = () => {
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);

  const uploadFile = async (file, folder) => {
    try {
      setError(null);
      setIsPending(true);

      const imageRef = storageRef(storage, `${folder}/${file.name}`);
      const uploadedRef = await uploadBytes(imageRef, file).then((snapshot) => snapshot.ref);
      const imageUrl = await getDownloadURL(uploadedRef);

      setIsPending(false);
      return imageUrl;
    } catch (error) {
      console.log(error.message);
      setError(error.message);
      setIsPending(false);
    }
  };

  /** Upload a file and also generate + upload a thumbnail. Returns { imageUrl, thumbnailUrl }. */
  const uploadFileWithThumbnail = async (file, folder, thumbMaxWidth = 400) => {
    try {
      setError(null);
      setIsPending(true);

      // Upload original
      const imageRef = storageRef(storage, `${folder}/${file.name}`);
      const uploadedRef = await uploadBytes(imageRef, file).then((s) => s.ref);
      const imageUrl = await getDownloadURL(uploadedRef);

      // Generate and upload thumbnail
      let thumbnailUrl = null;
      const thumbBlob = await resizeImage(file, thumbMaxWidth);
      if (thumbBlob) {
        const baseName = file.name.replace(/\.[^.]+$/, "");
        const thumbRef = storageRef(storage, `${folder}/thumbs/${baseName}_thumb.webp`);
        const thumbUploaded = await uploadBytes(thumbRef, thumbBlob, { contentType: "image/webp" }).then((s) => s.ref);
        thumbnailUrl = await getDownloadURL(thumbUploaded);
      }

      setIsPending(false);
      return { imageUrl, thumbnailUrl };
    } catch (err) {
      console.log(err.message);
      setError(err.message);
      setIsPending(false);
      return { imageUrl: null, thumbnailUrl: null };
    }
  };

  const deleteFile = async (filePath) => {
    try {
      setError(null);
      setIsPending(true);

      const fileRef = storageRef(storage, filePath);
      await deleteObject(fileRef);
      setIsPending(false);
    } catch (error) {
      console.log(error.message);
      setError(error.message);
      setIsPending(false);
    }
  };
  return { error, isPending, uploadFile, uploadFileWithThumbnail, deleteFile };
};
